import { ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type {
  DeleteMemoMutationInput,
  Mutation,
  SaveMemoMutationInput,
} from '../../../generated/api.graphql';
import * as VPErrors from '../../../VPError';
import type { VPResult } from '../../VPResult';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import { CONNECTION_METADATA_FIELDS } from '../../connection/Api';
import type { ConnectionArguments } from '../../connection/Api';
import * as GqlConnection from '../../connection/GqlConnection';
import * as Memo from './Memo';
import * as MemoInput from './MemoInput';
import type { MemoSearchArguments } from './MemoSearchArguments';
import { MEMO_FIELDS } from './fragments';

/**
 * Calendar memos on AppSchema. Nothing here catches: `VPResult` covers only GraphQL-level outcomes,
 * as in `App/Event` and `App/TeamRole` — not `App/Genre`, which folds thrown errors into a result.
 */

const failure = (message: string): VPResult<never, VPErrors.VPError[]> => ({
  success: false,
  errors: [{ path: '', message }],
});

export const MEMOS_QUERY = gql`
  query Memos($after: String, $first: Int, $filter: MemoSearchArguments) {
    memos(after: $after, first: $first, filter: $filter) {
      edges {
        cursor
        node {
          ...MemoFields
        }
      }
      pageInfo {
        ...ConnectionMetadataFields
      }
    }
  }
  ${MEMO_FIELDS}
  ${CONNECTION_METADATA_FIELDS}
`;

export const SAVE_MEMO_MUTATION = gql`
  mutation saveMemo($input: SaveMemoMutationInput!) {
    saveMemo(input: $input) {
      memo {
        ...MemoFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${MEMO_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const DELETE_MEMO_MUTATION = gql`
  mutation deleteMemo($input: DeleteMemoMutationInput!) {
    deleteMemo(input: $input) {
      success
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

/**
 * One page of the memos overlapping the window. Walking the pages is the caller's call; an unsized
 * read gets the field's own default page (100). `network-only` so a re-read after a write is fresh.
 */
export const fetchMemos = async (
  client: ApolloClient<NormalizedCacheObject>,
  filter: MemoSearchArguments,
  pagination: Pick<ConnectionArguments, 'after' | 'first'> = {},
): Promise<VPResult<GqlConnection.GqlConnection<Memo.Memo>, VPErrors.VPError[]>> => {
  const variables: Record<string, unknown> = { filter };

  if (pagination.after !== undefined) {
    variables.after = pagination.after;
  }

  if (pagination.first !== undefined) {
    variables.first = pagination.first;
  }

  const { data } = await client.query({
    query: MEMOS_QUERY,
    fetchPolicy: 'network-only',
    variables,
  });

  if (!data?.memos) {
    return failure('Failed to fetch the memos.');
  }

  return { success: true, data: GqlConnection.fromGql(data.memos, Memo.fromGql) };
};

/** Creates when the input carries no `id`, updates when it does. */
export const saveMemo = async (
  client: ApolloClient<NormalizedCacheObject>,
  memo: MemoInput.MemoInput,
): Promise<VPResult<Memo.Memo, VPErrors.VPError[]>> => {
  // Typed rather than inlined: the `mutate<Mutation>` generic does not check `variables`.
  const input: SaveMemoMutationInput = { memo: MemoInput.toGqlInput(memo) };

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_MEMO_MUTATION,
    variables: { input },
  });

  const payload = data?.saveMemo;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.memo) {
    return failure('Failed to save the memo.');
  }

  return { success: true, data: Memo.fromGql(payload.memo) };
};

export const deleteMemo = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<VPResult<null, VPErrors.VPError[]>> => {
  const input: DeleteMemoMutationInput = { id };

  const { data } = await client.mutate<Mutation>({
    mutation: DELETE_MEMO_MUTATION,
    variables: { input },
  });

  const payload = data?.deleteMemo;

  if (payload?.success) {
    return { success: true, data: null };
  }

  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  return errors.length ? { success: false, errors } : failure('Failed to delete the memo.');
};
